import { useEffect, useMemo, useRef } from 'react';
import { reloadPage } from '../helpers/reload';
import { Button } from '../ui/atoms/Button';
import { LinkButton } from '../ui/atoms/LinkButton';
import { DEFAULT_LOCALE } from '../i18n/config';
import { readLocaleCookie } from '../i18n/cookie';
import { createI18n } from '../i18n/i18n';

/**
 * Root error page (a11y spec, app shell). The boundary sits outside the
 * router, auth and i18n providers, so this stands alone: it builds its own
 * i18next instance from the `locale` cookie and uses plain elements for
 * navigation (`<a href="/">` is a full reload on purpose, to drop broken state).
 */
export function ErrorFallback() {
  const i18n = useMemo(
    () => createI18n(readLocaleCookie() ?? DEFAULT_LOCALE),
    [],
  );
  const title = i18n.t('common:errorBoundary.title');
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    document.title = `${title} — Takove`;
    // The page the user was on is gone; put focus on the new heading.
    headingRef.current?.focus();
  }, [title]);

  return (
    <main className="min-h-screen bg-base-200 flex items-center justify-center p-4">
      <div className="card bg-base-100 shadow-xl w-full max-w-md">
        <div className="card-body items-center text-center gap-4">
          <h1 ref={headingRef} tabIndex={-1} className="card-title text-3xl">
            {title}
          </h1>
          <p role="alert">{i18n.t('common:errorBoundary.message')}</p>
          <div className="card-actions justify-center">
            <Button onPress={reloadPage}>
              {i18n.t('common:errorBoundary.reload')}
            </Button>
            <LinkButton href="/" variant="ghost">
              {i18n.t('common:errorBoundary.home')}
            </LinkButton>
          </div>
        </div>
      </div>
    </main>
  );
}
